// lesson-nav.js — prev/next navigable lesson-item resolution (Brief 138).
//
// The audio player's bottom-row « » arrows navigate between LESSON ITEMS
// (iOS KFEAudioPlayerCombineControlView playLastOne:/playNextOne:). "Navigable"
// means a visible squiggle-traversable row — gates, meters, and intros are NOT
// navigable targets (same filter the squiggle + lesson chart use, gating.js
// `isVisibleLessonItem`). Next is additionally disabled when it sits behind an
// unsatisfied gate (Brief 135 NAV-1/NAV-2 — never navigate into locked
// content); prev is always reachable (the learner already passed it).
//
// Pure + DOM-free so it unit-tests without an IndexedDB / fetch backend; the
// screen feeds it the lesson items plus the per-item completion + requiredPlay
// maps it has already read from the learner store.

import { buildSingleFolderTree, getInaccessibleItems, isVisibleLessonItem } from "./gating.js";
import { isGatingExemptId } from "./gating-config.js";
import { ITEM_TYPE_GATE } from "./content-items.js";

const METER_TYPES = new Set([16, 17, 18, 19]);

/** @param {any} type */
export function isGateType(type) {
  return type === "gate" || type === ITEM_TYPE_GATE;
}

/** @param {any} type */
export function isMeterType(type) {
  return type === "meter_test" || METER_TYPES.has(type);
}

/**
 * Strip the lesson-JSON id prefix ("item_103880" / "gate_124033") to the bare
 * numeric id so it lines up with itemId / completedCounts keys.
 * @param {string|number|undefined|null} id
 * @returns {number}
 */
export function bareItemId(id) {
  return Number(String(id ?? "").replace(/^(item_|gate_)/, ""));
}

/**
 * The authored requiredPlay for a lesson item, read straight off the lesson
 * JSON row (Brief 100 ships it on every non-gate item). 0 when absent.
 * @param {Array<{id?: string|number, requiredPlay?: number}>} lessonItems
 * @param {number} itemId
 * @returns {number}
 */
export function authoredRequiredPlay(lessonItems, itemId) {
  const target = Number(itemId);
  for (const it of lessonItems || []) {
    if (bareItemId(it?.id) === target && typeof it?.requiredPlay === "number") {
      return it.requiredPlay;
    }
  }
  return 0;
}

/**
 * Resolve the prev/next navigable item for `currentItemId` within a lesson.
 *
 * @param {Array<{id?: string|number, type?: string|number, isIntro?: boolean, requiredPlay?: number, gateRule?: any}>} lessonItems
 *   the lesson's ordered items, verbatim from the lesson JSON (gates/meters included).
 * @param {number} currentItemId  the bare numeric id of the item being played.
 * @param {{ gatingEnabled?: boolean, completedCounts?: Record<string, number>, requiredPlays?: Record<string, number|undefined> }} [state]
 *   `completedCounts` / `requiredPlays` keyed by bare numeric id (string keys);
 *   `requiredPlays` should already be the resolved max(authored, bumped) value
 *   per item where known (Brief 125). gatingEnabled defaults to true.
 * @returns {{ prevId: number|null, nextId: number|null, prevEnabled: boolean, nextEnabled: boolean }}
 */
export function computeItemNav(lessonItems, currentItemId, state = {}) {
  const gatingEnabled = state.gatingEnabled !== false;
  const completedCounts = state.completedCounts || {};
  const requiredPlays = state.requiredPlays || {};
  const items = Array.isArray(lessonItems) ? lessonItems : [];

  /** @type {import("./gating.js").ItemNode[]} */
  const nodes = items.map((it) => {
    const id = bareItemId(it?.id);
    const resolvedReq = requiredPlays[String(id)];
    return {
      id,
      isGate: isGateType(it?.type),
      isMeter: isMeterType(it?.type),
      isIntroItem: it?.isIntro === true,
      // Brief 150: defensive — honour an exemption flag at item level too.
      isGatingExempt: isGatingExemptId(id, /** @type {any} */ (it)?.excludeFromGating),
      gateRule: /** @type {any} */ (it?.gateRule),
      requiredPlay: resolvedReq ?? (typeof it?.requiredPlay === "number" ? it.requiredPlay : undefined),
    };
  });

  const visible = nodes.filter(isVisibleLessonItem);
  const idx = visible.findIndex((n) => n.id === Number(currentItemId));
  const prev = idx > 0 ? visible[idx - 1] : null;
  const next = idx >= 0 && idx < visible.length - 1 ? visible[idx + 1] : null;

  /** @type {Set<string>} */
  let inaccessible = new Set();
  if (gatingEnabled && next) {
    const folderId = "__lesson_nav";
    const tree = buildSingleFolderTree(folderId, nodes);
    inaccessible = new Set(
      getInaccessibleItems(folderId, { gatingEnabled: true, completedCounts }, tree).map(String),
    );
  }

  return {
    prevId: prev ? Number(prev.id) : null,
    nextId: next ? Number(next.id) : null,
    prevEnabled: !!prev,
    nextEnabled: !!next && !inaccessible.has(String(next.id)),
  };
}
